/**
 * Occasion content.
 *
 * Drives the /occasions hub and the per occasion landing pages
 * (e.g. /birthday-cards). Each occasion always has a unique hub blurb so the
 * hub page is substantive rather than thin. Occasions that have a full landing
 * page also carry a populated `page` object with answer-first copy, benefits,
 * and FAQ written to be quoted by search engines and AI assistants.
 *
 * Scaling pattern: to add a new occasion landing page, populate the `page`
 * object on its entry here, add one route in App.tsx, and add the path to
 * includedRoutes in vite.config.ts and to public/sitemap.xml.
 *
 * Copy rule: no em dashes or spaced hyphens in any customer-facing string
 * (kept clean for marketing tone). Hyphens inside compound words are fine.
 */

export interface OccasionFaq {
  q: string;
  a: string;
}

export interface OccasionPageContent {
  /** URL path for the landing page, e.g. "/birthday-cards". */
  path:            string;
  metaTitle:       string;
  metaDescription: string;
  h1:              string;
  /** Answer-first lead paragraph. Written to be quoted by AI search. */
  intro:           string;
  /** Tailored three step "how it works" for this occasion. */
  steps:           { title: string; body: string }[];
  /** Why MomentSent for this occasion. */
  benefits:        string[];
  faq:             OccasionFaq[];
  /** Optional matching message-ideas guide, e.g. "/what-to-write-in-a-birthday-card". */
  messageGuidePath?: string;
  /**
   * Optional override for the closing CTA. Defaults assume a recurring yearly
   * occasion ("Never miss a ___ again" / "every year"). One-off occasions like
   * graduation or thinking-of-you set these so the copy stays truthful.
   */
  ctaTitle?:       string;
  ctaBody?:        string;
}

export interface Occasion {
  /** Internal key, e.g. "birthday". */
  slug:     string;
  /** Display name, e.g. "Birthday". */
  occasion: string;
  emoji:    string;
  /** Unique one or two sentence description shown on the /occasions hub. */
  hubBlurb: string;
  /** Present only for occasions that have a full landing page. */
  page?:    OccasionPageContent;
}

export const OCCASIONS: Occasion[] = [

  // ── Birthday (flagship, full landing page) ─────────────────────────────────
  {
    slug:     'birthday',
    occasion: 'Birthday',
    emoji:    '🎂',
    hubBlurb: 'Add each person’s birthday once and MomentSent mails a real card every year, automatically, so you are never the one who forgets.',
    page: {
      path:            '/birthday-cards',
      metaTitle:       'Automatic Birthday Card Service | MomentSent Mails Real Cards',
      metaDescription: 'MomentSent is an automatic birthday card service that mails a real, physical card to the people you love every year. Add the date once, we remember and mail it on time. Free 14-day trial, no credit card.',
      h1:              'Automatic Birthday Cards, Mailed for You Every Year',
      intro:           'MomentSent is an automatic birthday card service that mails a real, physical birthday card to the people you love, every year, without you having to remember. Add their name and birthday once, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it arrives on time, then sends again automatically the next year.',
      steps: [
        {
          title: 'Add the birthday once',
          body:  'Enter their name and date of birth a single time. MomentSent remembers it for good, so you never have to set another reminder.',
        },
        {
          title: 'Choose a design and your message',
          body:  'Pick from dozens of birthday designs and write a personal note. Not sure what to say? Use our birthday message ideas for inspiration.',
        },
        {
          title: 'We mail a real card every year',
          body:  'MomentSent prints and mails a real card with postage included so it arrives on time. Next year it sends again, automatically, with no effort from you.',
        },
      ],
      benefits: [
        'Never miss a birthday again. Add the date once and the card sends every year, right on schedule.',
        'A real card in the mailbox, not an email or an e-card. Recipients hold something physical from you.',
        'Preview and edit before anything mails. We notify you ahead of each send so you can change the message or design.',
        'Dozens of birthday designs for every age and personality, from kids to milestone birthdays.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included.',
      ],
      faq: [
        {
          q: 'How does an automatic birthday card service work?',
          a: 'You add a person and their birthday one time. MomentSent stores the date, lets you choose a design and write a message, then prints and mails a real physical card with postage included so it arrives on time. Each following year it sends a card again automatically, so you never have to remember the date or visit a store.',
        },
        {
          q: 'Will the birthday card arrive on time?',
          a: 'Yes. MomentSent schedules and mails each card ahead of the birthday so it arrives on or before the day. We also notify you before every send so you can review or change the card in advance.',
        },
        {
          q: 'Can I see the birthday card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message at any time, and we send a reminder before each card mails so nothing goes out without your approval.',
        },
        {
          q: 'Are the birthday cards handwritten?',
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: 'How much does it cost to send birthday cards automatically?',
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: 'Can I send birthday cards to people in other countries?',
          a: 'Yes. MomentSent mails birthday cards to over 100 countries with postage included, so you can keep in touch with friends and family anywhere in the world.',
        },
      ],
      messageGuidePath: '/what-to-write-in-a-birthday-card',
    },
  },

  // ── Anniversary (full landing page) ──────────────────────────────────────────
  {
    slug:     'anniversary',
    occasion: 'Anniversary',
    emoji:    '💍',
    hubBlurb: 'Wedding, dating, or any milestone. Set the date and a real card arrives each year to mark how far you have come together.',
    page: {
      path:            '/anniversary-cards',
      metaTitle:       'Automatic Anniversary Card Service | MomentSent Mails Real Cards',
      metaDescription: 'MomentSent is an automatic anniversary card service that mails a real, physical card every year on your anniversary. Add the date once, we remember and mail it on time. Free 14-day trial, no credit card.',
      h1:              'Automatic Anniversary Cards, Mailed for You Every Year',
      intro:           'MomentSent is an automatic anniversary card service that mails a real, physical anniversary card every year, so the date never slips by. Add the anniversary once, whether it is a wedding, a first date, or any milestone you share, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it arrives on time, then sends again automatically the next year.',
      steps: [
        {
          title: 'Add the anniversary once',
          body:  'Enter the names and the date a single time. MomentSent remembers it for good, so you never have to set another reminder.',
        },
        {
          title: 'Choose a design and your message',
          body:  'Pick from dozens of anniversary designs and write a personal note. Need inspiration? Use our anniversary message ideas.',
        },
        {
          title: 'We mail a real card every year',
          body:  'MomentSent prints and mails a real card with postage included so it arrives on time. Next year it sends again, automatically, with no effort from you.',
        },
      ],
      benefits: [
        'Never forget your anniversary again. Add the date once and the card sends every year, right on schedule.',
        'A real card in the mailbox, not a last minute text or e-card. Something they can hold and keep.',
        'Preview and edit before anything mails. We notify you ahead of each send so you can change the message or design.',
        'Designs for every kind of anniversary, from the first year to a golden fiftieth.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included.',
      ],
      faq: [
        {
          q: 'How does an automatic anniversary card service work?',
          a: 'You add the anniversary date one time. MomentSent stores it, lets you choose a design and write a message, then prints and mails a real physical card with postage included so it arrives on time. Each following year it sends a card again automatically, so you never have to remember the date.',
        },
        {
          q: 'Will the anniversary card arrive on time?',
          a: 'Yes. MomentSent schedules and mails each card ahead of the date so it arrives on or before your anniversary. We also notify you before every send so you can review or change it in advance.',
        },
        {
          q: 'Can I see the card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message at any time, and we send a reminder before each card mails so nothing goes out without your approval.',
        },
        {
          q: 'Are the anniversary cards handwritten?',
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: 'How much does it cost to send anniversary cards automatically?',
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: 'Can I send anniversary cards to people in other countries?',
          a: 'Yes. MomentSent mails anniversary cards to over 100 countries with postage included, so distance is never a reason to miss the date.',
        },
      ],
      messageGuidePath: '/what-to-write-in-an-anniversary-card',
    },
  },

  // ── Christmas (full landing page) ──────────────────────────────────────────────
  {
    slug:     'christmas',
    occasion: 'Christmas',
    emoji:    '🎄',
    hubBlurb: 'Handle your entire holiday card list in one sitting. MomentSent mails real Christmas cards to everyone on it, so the season feels less like a scramble.',
    page: {
      path:            '/christmas-cards',
      metaTitle:       'Automatic Christmas Card Service | MomentSent Mails Real Cards',
      metaDescription: 'MomentSent is an automatic Christmas card service that mails real, physical holiday cards to everyone on your list. Set your list once, we print, address, and mail with postage included. Free 14-day trial, no credit card.',
      h1:              'Automatic Christmas Cards, Mailed to Your Whole List',
      intro:           'MomentSent is an automatic Christmas card service that mails real, physical holiday cards to everyone on your list, so the season feels less like a scramble. Add your recipients once, choose a design, and write your message. MomentSent prints, addresses, and mails a genuine card to each person with postage included so they arrive in time for the holidays, then sends again automatically next year.',
      steps: [
        {
          title: 'Build your Christmas list once',
          body:  'Add everyone you want to send a card to a single time. MomentSent saves your list, so next year is even easier.',
        },
        {
          title: 'Choose a design and your message',
          body:  'Pick from holiday designs and write your greeting. Want help? Use our Christmas message ideas.',
        },
        {
          title: 'We print and mail every card',
          body:  'MomentSent prints, addresses, and mails a real card to each person with postage included so they arrive in time for Christmas.',
        },
      ],
      benefits: [
        'Handle your entire holiday card list in one sitting, with no trip to the store and no stack of envelopes to address.',
        'Real cards in the mailbox, the kind people display on the mantel, not an email or e-card.',
        'Preview and edit before anything mails. We notify you ahead of the send so you can review your list and messages.',
        'Designs for every style of holiday greeting, from classic and elegant to warm and playful.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included, so far away family is covered.',
      ],
      faq: [
        {
          q: 'How does an automatic Christmas card service work?',
          a: 'You add your recipients and choose a design and message one time. MomentSent prints, addresses, and mails a real physical card to each person with postage included so they arrive in time for the holidays. You can save your list and send again next year with just a few clicks.',
        },
        {
          q: 'Will my Christmas cards arrive in time?',
          a: 'Yes. MomentSent schedules holiday mailings so cards arrive before Christmas. We notify you ahead of the send so you can review your list and messages well in advance.',
        },
        {
          q: 'Can I send to my whole list at once?',
          a: 'Yes. Add as many recipients as you like and MomentSent prints, addresses, and mails a card to each one, so you handle the entire list in a single sitting.',
        },
        {
          q: 'Can I see the cards before they mail?',
          a: 'Yes. You can preview and edit the design and message any time, and we send a reminder before the cards mail so nothing goes out without your approval.',
        },
        {
          q: 'Are the Christmas cards handwritten?',
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: 'How much does it cost to mail Christmas cards?',
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
      ],
      messageGuidePath: '/what-to-write-in-a-christmas-card',
      ctaTitle: 'Cross the whole list off in one sitting.',
      ctaBody:  'Add your recipients once and MomentSent prints, addresses, and mails a real card to each person, with postage included, so they arrive in time for the holidays.',
    },
  },

  // ── Valentine's Day (full landing page) ────────────────────────────────────────
  {
    slug:     'valentines-day',
    occasion: "Valentine's Day",
    emoji:    '💝',
    hubBlurb: 'A real card in the mailbox says more than a text. Schedule a Valentine for the people you love most and let it arrive right on the 14th.',
    page: {
      path:            '/valentines-day-cards',
      metaTitle:       "Automatic Valentine's Day Card Service | MomentSent Mails Real Cards",
      metaDescription: "MomentSent is an automatic Valentine's Day card service that mails a real, physical card to the people you love, arriving on time every year. Set it once, we print and mail with postage included. Free 14-day trial.",
      h1:              "Automatic Valentine's Day Cards, Mailed Right on Time",
      intro:           "MomentSent is an automatic Valentine's Day card service that mails a real, physical card to the people you love, arriving right on the 14th, every year. Add your recipients once, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it lands on time, then sends again automatically next year.",
      steps: [
        {
          title: 'Add the people you love once',
          body:  'Add your partner, your kids, anyone you want to surprise, a single time. MomentSent remembers them for next year too.',
        },
        {
          title: 'Choose a design and your message',
          body:  'Pick from romantic and playful Valentine designs and write a personal note.',
        },
        {
          title: 'We mail a real card on time',
          body:  "MomentSent prints and mails a real card with postage included so it arrives by Valentine's Day. Next year it sends again, automatically.",
        },
      ],
      benefits: [
        'A real card in the mailbox says more than a text. Something they can hold and keep.',
        "Never forget Valentine's Day again. Set it once and the card arrives on time, every year.",
        'Preview and edit before anything mails. We notify you ahead of the send so you can change the message or design.',
        'Designs for every kind of love, from romantic to sweet and lighthearted, for partners, kids, and friends.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included.',
      ],
      faq: [
        {
          q: "How does an automatic Valentine's Day card service work?",
          a: 'You add your recipients one time and choose a design and message. MomentSent prints and mails a real physical card with postage included so it arrives by February 14th, and it sends again automatically each year.',
        },
        {
          q: 'Will the Valentine arrive on time?',
          a: "Yes. MomentSent schedules and mails each card ahead of Valentine's Day so it arrives on or before the 14th. We notify you before every send so you can review or change it.",
        },
        {
          q: 'Can I see the card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message any time, and we send a reminder before each card mails so nothing goes out without your approval.',
        },
        {
          q: "Are the Valentine's cards handwritten?",
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: "How much does it cost to send Valentine's cards?",
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: "Can I send Valentine's cards to people in other countries?",
          a: 'Yes. MomentSent mails to over 100 countries with postage included.',
        },
      ],
    },
  },

  // ── Mother's Day (full landing page) ───────────────────────────────────────────
  {
    slug:     'mothers-day',
    occasion: "Mother's Day",
    emoji:    '🌸',
    hubBlurb: 'Be the one who never forgets. MomentSent mails a real Mother’s Day card on time, every year, to the moms and mother figures in your life.',
    page: {
      path:            '/mothers-day-cards',
      metaTitle:       "Automatic Mother's Day Card Service | MomentSent Mails Real Cards",
      metaDescription: "MomentSent is an automatic Mother's Day card service that mails a real, physical card to the moms in your life, on time every year. Set it once, we print and mail with postage included. Free 14-day trial, no credit card.",
      h1:              "Automatic Mother's Day Cards, Mailed on Time Every Year",
      intro:           "MomentSent is an automatic Mother's Day card service that mails a real, physical card to the moms and mother figures in your life, arriving on time every year. Add them once, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it arrives for Mother's Day, then sends again automatically the next year.",
      steps: [
        {
          title: 'Add the moms in your life once',
          body:  'Add your mom, grandma, wife, or any mother figure a single time. MomentSent remembers them for next year too.',
        },
        {
          title: 'Choose a design and your message',
          body:  "Pick from heartfelt Mother's Day designs and write a personal note.",
        },
        {
          title: 'We mail a real card on time',
          body:  "MomentSent prints and mails a real card with postage included so it arrives for Mother's Day. Next year it sends again, automatically.",
        },
      ],
      benefits: [
        'Be the one who never forgets. Set it once and a real card arrives on time, every year.',
        'A real card she can hold and keep, not an email or e-card.',
        'Preview and edit before anything mails. We notify you ahead of the send so you can change the message or design.',
        'Heartfelt designs for moms, grandmas, wives, and every mother figure.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included, so far away moms are covered.',
      ],
      faq: [
        {
          q: "How does an automatic Mother's Day card service work?",
          a: "You add the moms in your life one time and choose a design and message. MomentSent prints and mails a real physical card with postage included so it arrives for Mother's Day, and it sends again automatically each year.",
        },
        {
          q: "Will the Mother's Day card arrive on time?",
          a: "Yes. MomentSent schedules and mails each card ahead of Mother's Day so it arrives on or before the day. We notify you before every send so you can review or change it.",
        },
        {
          q: 'Can I see the card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message any time, and we send a reminder before each card mails so nothing goes out without your approval.',
        },
        {
          q: "Are the Mother's Day cards handwritten?",
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: "How much does it cost to send Mother's Day cards?",
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: "Can I send a Mother's Day card to another country?",
          a: 'Yes. MomentSent mails to over 100 countries with postage included, so distance is never a reason to miss the day.',
        },
      ],
    },
  },

  // ── Father's Day (full landing page) ───────────────────────────────────────────
  {
    slug:     'fathers-day',
    occasion: "Father's Day",
    emoji:    '👔',
    hubBlurb: 'A card he can actually hold. Set it once and a real Father’s Day card arrives on schedule, year after year.',
    page: {
      path:            '/fathers-day-cards',
      metaTitle:       "Automatic Father's Day Card Service | MomentSent Mails Real Cards",
      metaDescription: "MomentSent is an automatic Father's Day card service that mails a real, physical card to the dads in your life, on time every year. Set it once, we print and mail with postage included. Free 14-day trial, no credit card.",
      h1:              "Automatic Father's Day Cards, Mailed on Time Every Year",
      intro:           "MomentSent is an automatic Father's Day card service that mails a real, physical card to the dads and father figures in your life, arriving on time every year. Add them once, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it arrives for Father's Day, then sends again automatically the next year.",
      steps: [
        {
          title: 'Add the dads in your life once',
          body:  'Add your dad, grandpa, husband, or any father figure a single time. MomentSent remembers them for next year too.',
        },
        {
          title: 'Choose a design and your message',
          body:  "Pick from Father's Day designs and write a personal note.",
        },
        {
          title: 'We mail a real card on time',
          body:  "MomentSent prints and mails a real card with postage included so it arrives for Father's Day. Next year it sends again, automatically.",
        },
      ],
      benefits: [
        'A card he can actually hold, not an email or e-card.',
        'Be the one who never forgets. Set it once and a real card arrives on time, every year.',
        'Preview and edit before anything mails. We notify you ahead of the send so you can change the message or design.',
        'Designs for dads, grandpas, husbands, and every father figure.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included, so far away dads are covered.',
      ],
      faq: [
        {
          q: "How does an automatic Father's Day card service work?",
          a: "You add the dads in your life one time and choose a design and message. MomentSent prints and mails a real physical card with postage included so it arrives for Father's Day, and it sends again automatically each year.",
        },
        {
          q: "Will the Father's Day card arrive on time?",
          a: "Yes. MomentSent schedules and mails each card ahead of Father's Day so it arrives on or before the day. We notify you before every send so you can review or change it.",
        },
        {
          q: 'Can I see the card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message any time, and we send a reminder before each card mails so nothing goes out without your approval.',
        },
        {
          q: "Are the Father's Day cards handwritten?",
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: "How much does it cost to send Father's Day cards?",
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: "Can I send a Father's Day card to another country?",
          a: 'Yes. MomentSent mails to over 100 countries with postage included, so distance is never a reason to miss the day.',
        },
      ],
    },
  },

  // ── Graduation (full landing page, one-off) ────────────────────────────────────
  {
    slug:     'graduation',
    occasion: 'Graduation',
    emoji:    '🎓',
    hubBlurb: 'Celebrate the big achievement with a real card in the mail. Perfect for grads near and far, with no last minute store run.',
    page: {
      path:            '/graduation-cards',
      metaTitle:       'Graduation Card Mailing Service | MomentSent Mails Real Cards',
      metaDescription: 'MomentSent mails a real, physical graduation card to the grad in your life, printed and posted for you with postage included so it arrives on time. Free 14-day trial, no credit card required.',
      h1:              'Graduation Cards, Printed and Mailed for You',
      intro:           'MomentSent mails a real, physical graduation card to the grad in your life, so you can celebrate the big achievement without a last minute trip to the store. Add their name and the date, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it arrives in time for the big day.',
      steps: [
        {
          title: 'Add the grad and the date',
          body:  'Enter their name and when you want the card to arrive. MomentSent handles the timing.',
        },
        {
          title: 'Choose a design and your message',
          body:  'Pick from graduation designs and write a note worthy of the milestone.',
        },
        {
          title: 'We mail a real card on time',
          body:  'MomentSent prints and mails a real card with postage included so it arrives in time to celebrate.',
        },
      ],
      benefits: [
        'A real card in the mailbox to mark a milestone that deserves more than a text.',
        'Perfect for grads near and far, with no last minute store run and no postage to buy.',
        'Preview and edit before anything mails. We notify you ahead of the send so you can change the message or design.',
        'Designs for every graduation, from high school and college to advanced degrees.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included.',
      ],
      faq: [
        {
          q: 'How does MomentSent mail a graduation card?',
          a: "You add the grad's name, the date you want the card to arrive, a design, and your message. MomentSent prints and mails a real physical card with postage included so it arrives on time, with no trip to the store.",
        },
        {
          q: 'Will the graduation card arrive on time?',
          a: 'Yes. MomentSent schedules the mailing so the card arrives in time for the celebration. We notify you before it sends so you can review or change it.',
        },
        {
          q: 'Can I see the card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message any time, and we send a reminder before the card mails so nothing goes out without your approval.',
        },
        {
          q: 'Is the graduation card handwritten?',
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: 'How much does it cost to mail a graduation card?',
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: 'Can I send a graduation card to another country?',
          a: 'Yes. MomentSent mails graduation cards to over 100 countries with postage included.',
        },
      ],
      ctaTitle: 'Celebrate the grad with a real card.',
      ctaBody:  'Add the date and your message, and MomentSent prints and mails a real card so it arrives right on time, with no last minute store run.',
    },
  },

  // ── Thinking of You (full landing page, any date) ──────────────────────────────
  {
    slug:     'thinking-of-you',
    occasion: 'Thinking of You',
    emoji:    '💌',
    hubBlurb: 'Just because. Send a real card on any date to let someone know they are on your mind, no occasion required.',
    page: {
      path:            '/thinking-of-you-cards',
      metaTitle:       'Thinking of You Card Service | MomentSent Mails Real Cards',
      metaDescription: 'MomentSent mails a real, physical thinking of you card on any date you choose, printed and posted for you with postage included. No occasion required. Free 14-day trial, no credit card.',
      h1:              'Thinking of You Cards, Mailed on Any Day',
      intro:           'MomentSent mails a real, physical thinking of you card on any date you choose, so you can let someone know they are on your mind, no occasion required. Add their name, pick a date, choose a design, and write your message. MomentSent prints and mails a genuine card with postage included so it arrives right when you want it to.',
      steps: [
        {
          title: 'Pick the person and the date',
          body:  'Choose who to send to and when you want the card to arrive. Any day works, no occasion needed.',
        },
        {
          title: 'Choose a design and your message',
          body:  'Pick from warm, simple designs and write a few words from the heart.',
        },
        {
          title: 'We mail a real card for you',
          body:  'MomentSent prints and mails a real card with postage included so it arrives right on time.',
        },
      ],
      benefits: [
        "Brighten someone's day for no reason at all. A real card just because means more than a text.",
        'Send on any date you choose, with no occasion required.',
        'Preview and edit before anything mails. We notify you ahead of the send so you can change the message or design.',
        'Warm, simple designs for any moment you want to reach out.',
        'Your own message, printed inside in a warm handwriting-style font.',
        'Mails worldwide to over 100 countries, with postage always included.',
      ],
      faq: [
        {
          q: 'How does a thinking of you card service work?',
          a: 'You choose a recipient, a date, a design, and your message. MomentSent prints and mails a real physical card with postage included so it arrives on the day you pick, no occasion required.',
        },
        {
          q: 'Can I send a card on any day?',
          a: 'Yes. There is no occasion required. Pick any date and MomentSent mails a real card so it arrives right when you want it to.',
        },
        {
          q: 'Can I see the card before it is mailed?',
          a: 'Yes. You can preview and edit the design and message any time, and we send a reminder before the card mails so nothing goes out without your approval.',
        },
        {
          q: 'Is the card handwritten?',
          a: 'Your message is printed inside each real folded card in a warm, handwriting-style font, so it reads personal and natural. It is printed rather than written in ink, and you write it once before we print and mail the card for you.',
        },
        {
          q: 'How much does it cost?',
          a: 'Plans start at $7.99 a month with a free 14-day trial and no credit card required. Each plan includes a yearly allowance of folded cards (12, 24, or 48) with printing and first-class postage included; extra cards beyond your allowance are a flat $5.99 each.',
        },
        {
          q: 'Can I send a card to another country?',
          a: 'Yes. MomentSent mails to over 100 countries with postage included.',
        },
      ],
      ctaTitle: 'Let someone know you are thinking of them.',
      ctaBody:  'Pick a date and your message, and MomentSent mails a real card so it arrives right when you want it to, no occasion required.',
    },
  },

];

/** Look up an occasion that has a full landing page, by its landing-page path. */
export function getOccasionByPath(path: string): Occasion | undefined {
  return OCCASIONS.find((o) => o.page?.path === path);
}

/** Look up an occasion by its internal slug. */
export function getOccasion(slug: string | undefined): Occasion | undefined {
  if (!slug) return undefined;
  return OCCASIONS.find((o) => o.slug === slug);
}
